import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { storageSize } from '@wr/shared';
import { offline as rules } from '../../../../../config/portal.json';
import { useCurrentEvent } from '../../components/CurrentEvent.js';
import { useOffline } from '../../offline/context.js';
import { keptItems, preparation, type KeptItem } from '../../offline/media.js';
import { useOnline } from '../../offline/network.js';
import { useSignedIn } from '../../session.js';
import { loadKiosk, type KioskItem } from '../kiosk/data.js';

interface ShowLike {
  id: string;
  status?: unknown;
  starts_on?: unknown;
  ends_on?: unknown;
  kiosk_asset_ids?: unknown;
}

const day = (v: unknown) => (typeof v === 'string' && v ? v.slice(0, 10) : null);
const localToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** A show coming (within `offline.prepareDaysBefore` days) or happening: time to prepare. */
export function showIsNear(e: ShowLike, today = localToday()): boolean {
  const start = day(e.starts_on);
  if (e.status === 'completed' || !start) return false;
  const end = day(e.ends_on) ?? start;
  const from = new Date(`${start}T00:00:00Z`);
  from.setUTCDate(from.getUTCDate() - rules.prepareDaysBefore);
  return today >= from.toISOString().slice(0, 10) && today <= end;
}

/**
 * Before a show (round 2, brief section 7.4), on its card in Events and in
 * Travel: "Prepare offline content before you leave", with this device's
 * state. Ready means the show's booth kiosk content is on this device (for
 * those who start the kiosk), else that some content is kept here.
 */
export function PrepareOffline({ event, name }: { event: ShowLike; name?: string }) {
  const { store } = useOffline();
  const { can } = useSignedIn();
  const { events } = useCurrentEvent();
  const online = useOnline();
  const navigate = useNavigate();
  const near = showIsNear(event);
  // Travel's shows do not carry their kiosk content: the device's copy of the event does.
  const chosen = event.kiosk_asset_ids ?? events.find((e) => e.id === event.id)?.kiosk_asset_ids;
  const kioskIds = can('kiosk.start') && Array.isArray(chosen) ? (chosen as string[]) : [];
  const hasKiosk = kioskIds.length > 0;
  const kioskKey = kioskIds.join(',');
  const [state, setState] = useState<{
    kept: Record<string, KeptItem>;
    kiosk: KioskItem[];
  } | null>(null);

  useEffect(() => {
    if (!near || !store) return;
    let current = true;
    void (async () => {
      const kept = await keptItems(store).catch(() => ({}));
      const kiosk = hasKiosk ? await loadKiosk(store, event.id).catch(() => []) : [];
      if (current) setState({ kept, kiosk });
    })();
    return () => {
      current = false;
    };
  }, [near, store, event.id, hasKiosk, kioskKey]);

  if (!near || !state) return null;
  const prep = preparation(state.kiosk, state.kept);
  const others = Object.keys(state.kept).filter((id) => !kioskIds.includes(id)).length;
  const ready = hasKiosk ? prep.ready && state.kiosk.length > 0 : others > 0;
  const details = [
    hasKiosk &&
      (prep.ready
        ? 'Booth kiosk ready offline'
        : `Booth kiosk: ${storageSize(prep.size)} to download`),
    hasKiosk &&
      prep.noCopy > 0 &&
      `${prep.noCopy} video${prep.noCopy === 1 ? '' : 's'} without offline copy`,
    others > 0
      ? `${others} item${others === 1 ? '' : 's'} from Content on this device`
      : 'Nothing from Content on this device yet',
  ].filter(Boolean);
  return (
    <div className="prep-off" role="group" aria-label="Prepare offline content">
      <span className="prep-off-t">
        <b>Prepare offline content before you leave{name ? `: ${name}` : ''}</b>
        <span className={ready ? 'trip-ok' : 'trip-gap'}>
          {ready ? 'Ready on this device' : 'Not ready on this device'}
        </span>
      </span>
      <span className="note">{details.join('. ')}.</span>
      <span className="actions">
        {hasKiosk && !prep.ready && (
          <button
            type="button"
            className="btn sm"
            disabled={!online}
            onClick={() => navigate(`/kiosk/${encodeURIComponent(event.id)}`)}
          >
            Download booth kiosk ({storageSize(prep.size)})
          </button>
        )}
        <button
          type="button"
          className="btn sm"
          onClick={() => navigate(others > 0 ? '/account#offline' : '/content')}
        >
          {others > 0 ? 'Offline content' : 'Choose in Content'}
        </button>
      </span>
    </div>
  );
}
